import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";
import { startDownload } from "./GuidePdf";
import { EmailGate, savedContact } from "./EmailGate";
import { useT, useLang } from "@/i18n";
import { track } from "@/lib/track";
import DATA from "@/data/lamellare.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEYS = ["tecnica", "classi", "dimensioni", "xlam"];
const sheetUrl = (key, lang) => `/docs/schede/scheda-${key}-${lang}.pdf`;
const catalogSheet = (t) => ({ title: t("cat.title", "Catalogo strutture in legno"), sub: t("cat.sub", "Lamellare BSH, curvo, solai e pannelli X-LAM con foto e dati tecnici"), noWork: true });
const sheetOf = (k, d, t) => (k === "catalogo" ? catalogSheet(t) : d.sheets[k]);

const SheetCard = ({ k, d, t, onPick }) => (
  <button onClick={() => onPick(k)} className="group flex flex-col items-start border border-white/15 p-6 text-left transition-colors duration-300 hover:border-[#D98A52]" data-testid={`sheet-card-${k}`}>
    <FileText className="h-7 w-7 text-[#D98A52]" />
    <h3 className="mt-5 font-display text-xl font-bold">{sheetOf(k, d, t).title}</h3>
    <p className="mt-2 text-sm text-[#D6D0C4]">{sheetOf(k, d, t).sub}</p>
    {!sheetOf(k, d, t).noWork && <p className="mt-1 text-sm text-[#D6D0C4]">+ {d.work.title}</p>}
    <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">
      <Download className="h-4 w-4" /> PDF
    </span>
  </button>
);

export const SheetDownloads = ({ keys = KEYS }) => {
  const t = useT();
  const { lang } = useLang();
  const d = DATA[lang] ?? DATA.it;
  const [open, setOpen] = useState(null);
  const [done, setDone] = useState(null);
  const log = (k, form) => axios.post(`${API}/sheet-leads`, { ...form, sheet: k, lang });
  const pick = (k) => {
    const contact = savedContact();
    if (contact) {
      log(k, { ...contact, consent: true }).catch(() => {});
      track("download_scheda", { scheda: k, lingua: lang });
      return startDownload(sheetUrl(k, lang));
    }
    setDone(null);
    setOpen(k);
  };
  const submit = async (form) => {
    const res = await log(open, form);
    track("download_scheda", { scheda: open, lingua: lang });
    const url = res.data?.url ?? sheetUrl(open, lang);
    setDone(url);
    startDownload(url);
    toast.success(t("sheets.ok", "Grazie! Il download è partito e ti abbiamo inviato le schede anche per email."));
  };
  return (
    <section id="schede" className="bg-[#121110] px-5 py-24 text-[#F3EFE6] sm:px-8" data-testid="sheets-section">
      <div className="mx-auto max-w-[1500px]">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D98A52]">{t("sheets.label", "Schede di lavoro in PDF")}</p>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("sheets.h", "Scarica le schede e usale per il tuo progetto.")}</h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#D6D0C4]">{t("sheets.p", "Ogni scheda contiene i dati tecnici e una scheda di lavoro da compilare con i dati del progetto e inviare al nostro ufficio tecnico. Lascia i tuoi dati una sola volta per scaricarle tutte.")}</p>
        <div className={`mt-12 grid gap-4 ${keys.length > 4 ? "sm:grid-cols-2 lg:grid-cols-5" : keys.length > 1 ? "sm:grid-cols-2 lg:grid-cols-4" : "max-w-md"}`}>
          {keys.map((k) => <SheetCard key={k} k={k} d={d} t={t} onPick={pick} />)}
        </div>
      </div>
      <EmailGate
        open={!!open}
        onClose={() => setOpen(null)}
        title={open ? sheetOf(open, d, t).title : ""}
        description={t("sheets.gate2", "Lascia nome, cognome, email e telefono per scaricare la scheda in PDF: ti inviamo anche tutte le schede per email.")}
        submitLabel={t("sheets.submit", "Scarica la scheda")}
        onSubmit={submit}
        doneHref={done}
        doneLabel={t("pdf.again", "Scarica il PDF")}
      />
    </section>
  );
};
